import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const getDefaultMessages = (t: (key: string) => string) => [
  t("loadingTimelyMessager.uploadingFile"),
  t("loadingTimelyMessager.analyzingDocument"),
  t("loadingTimelyMessager.extractingInformation"),
  t("loadingTimelyMessager.finalizingResults"),
  t("loadingTimelyMessager.justAMoment"),
];

const defaultDurationMs = 4000;
const penultimateDurationMs = 8000;
const defaultFadeDurationMs = 200;

type LoadingTimelyMessagerProps = {
  isLoading?: boolean;
  messages?: string[];
  className?: string;
};

const LoadingTimelyMessager = ({
  isLoading = true,
  className,
}: LoadingTimelyMessagerProps) => {
  const { t } = useTranslation();
  const messages = useMemo(
    () => getDefaultMessages(t as (key: string) => string),
    [t],
  );
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const stepTimeoutRef = useRef<number | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const fadeDurationMs = defaultFadeDurationMs;

    const clearTimers = () => {
      if (stepTimeoutRef.current) {
        window.clearTimeout(stepTimeoutRef.current);
        stepTimeoutRef.current = null;
      }

      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };

    if (!isLoading || messages.length === 0) {
      clearTimers();
      setIsVisible(false);
      return;
    }

    const lastIndex = messages.length - 1;

    const scheduleNext = (currentIndex: number) => {
      if (currentIndex >= lastIndex) return;

      const durationMs =
        currentIndex === lastIndex - 1
          ? penultimateDurationMs
          : defaultDurationMs;

      stepTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);

        fadeTimeoutRef.current = window.setTimeout(() => {
          const nextIndex = Math.min(currentIndex + 1, lastIndex);
          setIndex(nextIndex);
          setIsVisible(true);
          scheduleNext(nextIndex);
        }, fadeDurationMs);
      }, durationMs);
    };

    setIndex(0);
    setIsVisible(true);
    clearTimers();
    scheduleNext(0);

    return () => {
      clearTimers();
    };
  }, [isLoading, messages]);

  if (!messages.length || !isLoading) return null;

  return (
    <span
      className={
        className ??
        [
          "transition-opacity duration-300 ease-in-out",
          isVisible ? "opacity-100" : "opacity-0",
        ].join(" ")
      }
    >
      {messages[index]}
    </span>
  );
};

export default LoadingTimelyMessager;
